import errorHandlers from '@/services/error-handlers';
import http from '@/services/http';
import normalizePaymentRequestBody from '@/helpers/normalize-payment-request-body';
import store from '@/store';
import parseCapiError from '@/helpers/parse-capi-error';
import resolveCloverPaymentActions from '@/helpers/resolve-clover-payment-actions';



const pay = {
  async addPayment({
    completePayment = true,
    contactInfo,
    order,
    paymentMethod,
    receiptToken,
    returnFirstPayment = false,
    tip,
    tipDepartmentPublicId,
    tipEmployeePublicId,
    total
  } = {}) {
    try {
      const orderRoom = store.getters['socket/orderRoom'];
      const selectedLocation = store.getters['locations/selectedLocation'];
      const { tdsData, cloverBrowserInfo } = store.getters['orders/order'];

      const body = await normalizePaymentRequestBody(
        {
          contactInfo,
          paymentGateway: selectedLocation.paymentGateway,
          paymentMethod,
          ...tdsData && { tdsData },
          ...cloverBrowserInfo && { cloverBrowserInfo }
        },
        {
          clientReferenceKey: orderRoom.you.id,
          itemDetailData: pay.returnYourSelectedItemIds(),
          receiptToken, /* used to link a new payment to an existing
          payment when paying for an order using multiple forms of payment */
          serviceCharges: orderRoom.you.serviceCharges,
          storeFees: orderRoom.you.orderFeeTotal,
          tax: orderRoom.you.tax,
          tip,
          tipDepartmentPublicId,
          tipEmployeePublicId,
          total: total || orderRoom.you.total
        }
      );

      store.dispatch('session/updateEmailForReceipt', body.email);
      store.dispatch('session/updateSmsNumberForReceipt', body.phoneNumber);

      /** One-shot: clear so the browser info can't leak onto a later non-3DS retry. */
      if (cloverBrowserInfo) store.commit('orders/SET_CLOVER_BROWSER_INFO');

      if (window._debugPay) {
        window._paymentRequestBody = body;
        return;
      }
      let data = await pay.postPayment({ orderId: order.orderId, body });

      /** Only Clover 3DS payments have paymentActions to resolve. */
      if (cloverBrowserInfo) data = await pay.resolvePaymentActions({ order, data });

      const { payments, hasSufficientPayments } = data;

      let returnValue;

      if (returnFirstPayment) {
        const [firstPayment] = payments;
        returnValue = firstPayment;
      }
      else if (completePayment) {
        const yourPayment = pay.completePayment({ hasSufficientPayments, payments });
        returnValue = yourPayment;
      }

      return returnValue;
    }

    catch (error) {
      await pay.handlePaymentError(error);

      throw error;
    }
  },

  completePayment({ hasSufficientPayments, payments }) {
    const orderRoom = store.getters['socket/orderRoom'];
    const yourPayment = payments.find(p => p.clientReferenceKey === orderRoom.you.id);

    store.dispatch('socket/payment', {
      orderWasClosed: hasSufficientPayments,
      payment: yourPayment
    });

    return yourPayment;
  },

  async postPayment({ orderId, body }) {
    const { data } = await http.post(`/cardfree-api/v1/account/me/order/${orderId}/payment`, body);

    return data;
  },

  /**
   * Clover 3DS runs as a post-payment action loop (see resolveCloverPaymentActions). Here your
   * payment is located by the socket order room's client reference, and Confirms are resubmitted
   * to the payment endpoint.
   */
  async resolvePaymentActions({ order, data }) {
    const yourId = store.getters['socket/orderRoom'].you.id;
    const yourPayment = (data?.payments || []).find(p => p.clientReferenceKey === yourId);

    return resolveCloverPaymentActions({
      data,
      yourPayment,
      postConfirm: body => pay.postPayment({ orderId: order.orderId, body })
    });
  },

  async editAndCompletePayments({ order, payments }) {
    try {
      const { data } = await http.post(`/cardfree-api/v1/account/me/order/${order.orderId}/payment/complete`, { payments });

      return data.payments;
    }

    catch (error) {
      await pay.handlePaymentError(error);

      throw error;
    }
  },

  async handlePaymentError(error) {
    const { codes } = parseCapiError(error);
    const { you } = store.getters['socket/orderRoom'];

    /**
    * NOTE: For these error codes, if we see that payment was authorized (in the payments array),
    * we'll conditionally let users retry capture payment with a button.
    * If we don't get a payment back, we'll let the user try to pay again.
    */
    const paymentCaptureFailureCodes = [
      'complete-payment-failed-pos-unavailable',
      'order-closed',
      'order-fulfill-failed-without-payment-error',
      'order-fulfill-partial-payment-error',
      'order-paid-at-pos',
      'pos-degraded',
      'pos-generic-error',
      'store-unavailable',
      'ticket-locked-at-pos'
    ];

    switch (true) {
      case codes.includes('order-stale'):
        pay.handleStaleOrderError(error);
        break;

      case codes.includes('opentab-payment-capture-failure'):
        errorHandlers.generic(error);
        break;

      case paymentCaptureFailureCodes.some(code => codes.includes(code)):
        pay.handlePaymentCaptureFailureError(error, you);
        break;

      default:
        errorHandlers.generic(error, {
          important: true
        });
        break;
    }
  },

  /* this method hasn't been unit tested yet */
  async handleStaleOrderError(error) {
    /* NOTE: This occurs when payment was added while order was out of sync. */

    try {
      await store.dispatch('orders/getAllOrdersAtTable', {
        paymentConflict: true
      });
    }

    catch (syncError) {
      errorHandlers.silent(syncError);
      errorHandlers.generic(error, {
        important: true
      });
    }
  },

  /* this method hasn't been unit tested yet */
  handlePaymentCaptureFailureError(error, you) {
    const data = error.response.data.data || {};
    const payments = data.payments || [];
    const yourPayment = payments.find(p => p.clientReferenceKey === you.id);

    if (yourPayment) {
      store.dispatch('socket/paymentCaptureFailure', {
        capiErrorResponse: error.response,
        payment: yourPayment
      });

      errorHandlers.silent(error);
    }

    else {
      errorHandlers.generic(error, {
        important: true
      });
    }
  },

  returnYourSelectedItemIds() {
    const { orderIsSplit, splitType, you } = store.getters['socket/orderRoom'];

    if (orderIsSplit && splitType === 'itemized') {
      return you.items.map(item => item.lineItemId);
    }

    return null;
  }
};



export default pay;
